<script setup lang="ts">
import type { Schema } from "./epost-login.js";
import mgr from "~/telenorid/telenorid";
import defaults from "./epostLoginDefaults";
import { useNotificationStore } from "~/pinia/platform/notifications/notifications";
import EpostLoginForm from "./components/EpostLoginForm.vue";
import { useSessionStorage } from "@vueuse/core";

const props = defineProps<Schema>();

const notification = useNotificationStore();

const isLoadingSession = ref(true);
const showForm = useSessionStorage<boolean>("showForm", false);

const { titles, buttonsAndLinks, separators, loginInfoBox, errors, form } = props.content || {};

const router = useRouter();
const goToEpost = () => {
	const currentPath = router.currentRoute.value.path.replace(/\/$/, "");
	router.push(`${currentPath}/epost`);
};
const signedIn = ref(false);

const initializeSession = async () => {
	try {
		const user = await mgr.getOrLoginUser();
		signedIn.value = !!user;
	} catch (err) {
		console.error("Error during session initialization:", err);
		signedIn.value = false;
	} finally {
		isLoadingSession.value = false;
	}
};

onMounted(async () => {
	await initializeSession();
	const params = new URLSearchParams(window.location.search);
	if (params.get("loginError") === "1") {
		showForm.value = true;
	}
});

const telenorIdSignIn = async (callbackUrl: string) => {
	try {
		await mgr.loginRedirectBackHere({ from: "online" }, false, callbackUrl);
	} catch (err) {
		console.error("Error during Telenor ID login:", err);
		notification.error({
			title: "Innlogging feilet",
			message: errors?.telenorIdLoginError || defaults.errors.telenorIdLoginError,
		});
	}
};

const telenorIdSignOut = async (callbackUrl: string) => {
	try {
		await mgr.setCallbackRedirectUrl(callbackUrl);
		await mgr.signoutRedirect();
	} catch (err) {
		console.error("Error during Telenor ID logout:", err);
		notification.error({
			title: "Utlogging feilet",
			message: errors?.telenorIdLogoutError || defaults.errors.telenorIdLogoutError,
		});
	}
};
</script>

<template>
	<div class="email-wrapper">
		<div
			class="spinner-container"
			v-if="isLoadingSession"
		>
			<TnSpinner
				size="l"
				color="lightblue"
			/>
		</div>
		<div
			class="login-logout-container"
			v-else
		>
			<div
				class="email-login"
				v-if="!signedIn"
			>
				<h1>{{ titles?.componentTitle || defaults.titles.componentTitle }}</h1>
				<Transition
					name="slide"
					mode="out-in"
				>
					<div v-if="loginInfoBox?.showInfoBox && !showForm">
						<div class="spacing-container" />
						<TnNotificationInline
							v-if="loginInfoBox?.showInfoBox && !showForm"
							dark
							hide-icon
						>
							{{ loginInfoBox?.telenorIdLoginInfo }}
						</TnNotificationInline>
					</div>
				</Transition>
				<TnButton
					icon-left="telenor"
					:primary="true"
					:dark="true"
					class="margin-top-xl"
					id="telenor-id-btn"
					@click="telenorIdSignIn('/online/epost')"
				>
					{{ buttonsAndLinks?.loginWithTelenorIdText || defaults.buttonsAndLinks.loginWithTelenorIdText }}
				</TnButton>
				<EpostLoginForm
					v-if="form?.showForm"
					:content="content"
					v-model:showForm="showForm"
				/>
			</div>
			<div
				class="email-logout"
				v-else
			>
				<h1>{{ titles?.loggedInTitle || defaults.titles.loggedInTitle }}</h1>
				<TnParagraph
					class="margin-top-m"
					:dark="true"
					size="s"
				>
					{{ titles?.loggedInDescription || defaults.titles.loggedInDescription }}
				</TnParagraph>
				<TnButton
					:primary="true"
					:dark="true"
					class="margin-top-3xl"
					@click="goToEpost"
				>
					{{ buttonsAndLinks?.goToEpostText || defaults.buttonsAndLinks.goToEpostText }}
				</TnButton>
				<div class="separator">
					<span>{{ separators?.separatorTextWhenLoggedIn || defaults.separators.separatorTextWhenLoggedIn }}</span>
				</div>
				<TnButton
					:secondary="true"
					:dark="true"
					@click="() => telenorIdSignOut('/online')"
				>
					{{ buttonsAndLinks?.logoutText || defaults.buttonsAndLinks.logoutText }}
				</TnButton>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
// The SVG for the Telenor icon does use fill="currentColor" on the <svg>, but the actual <path> inside has a hardcoded fill="#00C8FF"
:deep(#telenor-id-btn svg g#Telenor path) {
	fill: inherit !important;
}

.separator {
	display: flex;
	justify-content: center;
	margin: $spacing-m 0;
}

.email-wrapper {
	width: 100%;
	padding: 70px 32px;
	min-height: 600px;
	background: var(--Surface-Inverted, #000b2e);
	color: #fff;
	display: flex;
	justify-content: center;
	align-items: center;
	@include breakpoint(mobile) {
		padding: 32px;
		min-height: 0;
	}
}

.spinner-container {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
}

h1 {
	@include font-title-s;
}

.login-logout-container {
	display: flex;
	flex-direction: column;
	flex: 1;
}
.email-login,
.email-logout {
	width: 100%;
	max-width: 500px;
	display: flex;
	flex-direction: column;
}

.email-logout {
	padding-top: 46px;
}

// Used for smooth spacing transition
.spacing-container {
	height: $spacing-xl;
}
.slide-enter-active,
.slide-leave-active {
	transition:
		max-height 0.1s,
		opacity 0.2s;
	max-height: 100vh;
}
.slide-enter-from,
.slide-leave-to {
	max-height: 0vh;
	overflow: hidden;
	opacity: 0;
}
</style>
